import { useEffect, useMemo, useRef } from 'react';
import { CareScoutWidget } from '@/features/care-costs/components/CareScoutWidget';
import { CARESCOUT_ATTRIBUTION } from '@/features/care-costs/constants/careCosts.constants';
import type { CareScoutWidgetErrorReason } from '@/features/care-costs/types/careCosts.types';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useCurrentFinancialAdvisorProfile } from '@/features/provider-directory/hooks/useFinancialAdvisorProfile';
import { trackRumAction, trackRumError } from '@/shared/observability/rum';

const FP_TAB_VIEW_EVENT = 'fp_care_cost_tab_view';
const FP_WIDGET_LOADED_EVENT = 'fp_care_cost_widget_loaded';
const FP_WIDGET_ERROR_EVENT = 'fp_care_cost_widget_error';

export const CostCalculatorTab = () => {
  const { user } = useAuth();
  const { data: advisorProfile } = useCurrentFinancialAdvisorProfile();

  const rumContext = useMemo(
    () => ({
      userId: user?.id ?? '',
      kindeId: user?.kindeId ?? '',
      financialAdvisorId: advisorProfile?.id ?? '',
    }),
    [user?.id, user?.kindeId, advisorProfile?.id],
  );

  const rumContextRef = useRef(rumContext);
  rumContextRef.current = rumContext;

  const hasTrackedTabViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedTabViewRef.current) return;

    const { userId, kindeId, financialAdvisorId } = rumContextRef.current;
    if (!userId || !kindeId || !financialAdvisorId) return;

    trackRumAction(FP_TAB_VIEW_EVENT, rumContextRef.current);
    hasTrackedTabViewRef.current = true;
  }, [rumContext.userId, rumContext.kindeId, rumContext.financialAdvisorId]);
  const handleWidgetLoaded = () => {
    trackRumAction(FP_WIDGET_LOADED_EVENT, rumContextRef.current);
  };

  const handleWidgetError = (reason: CareScoutWidgetErrorReason) => {
    trackRumError(new Error(`CareScout widget error: ${reason}`), {
      ...rumContextRef.current,
      errorReason: reason,
    });
    trackRumAction(FP_WIDGET_ERROR_EVENT, {
      ...rumContextRef.current,
      errorReason: reason,
    });
  };

  return (
    <section className="space-y-3">
      <CareScoutWidget onLoaded={handleWidgetLoaded} onError={handleWidgetError} />
      <p className="text-sm font-normal leading-relaxed text-muted-foreground">
        {CARESCOUT_ATTRIBUTION}
      </p>
    </section>
  );
};
